import * as reading from "@weights/plan-reading";
import type { PlanDraft, PublishBody, PublishedWeek, ReferenceSessionWire, RunReading, WeightsReading } from "@weights/plan-reading";
import * as text from "./text.js";

/**
 * The shared reader is being ported alongside the builder, so any of its functions may throw or
 * not be exported yet. Everything the UI calls goes through here and degrades instead of breaking.
 * The spread keeps the bundler from resolving names the package may not export yet.
 */
const pkg: Record<string, unknown> = { ...reading };

function fn<T extends (...args: never[]) => unknown>(name: string): T | undefined {
  const value = pkg[name];
  return typeof value === "function" ? (value as T) : undefined;
}

function stringHelper(name: string, fallback: (s: string) => string): (s: string) => string {
  return (s) => {
    const f = fn<(s: string) => unknown>(name);
    if (f) {
      try {
        const out = f(s);
        if (typeof out === "string") return out;
      } catch {
        /* fall back */
      }
    }
    return fallback(s);
  };
}

function boolHelper(name: string, fallback: (s: string) => boolean): (s: string) => boolean {
  return (s) => {
    const f = fn<(s: string) => unknown>(name);
    if (f) {
      try {
        const out = f(s);
        if (typeof out === "boolean") return out;
      } catch {
        /* fall back */
      }
    }
    return fallback(s);
  };
}

export const labelKey = stringHelper("labelKey", text.labelKey);
export const displayLabel = stringHelper("displayLabel", text.displayLabel);
export const looksLikeLabel = boolHelper("looksLikeLabel", text.looksLikeLabel);
export const isPrepLabel = boolHelper("isPrepLabel", text.isPrepLabel);
export const isWeightsLabel = boolHelper("isWeightsLabel", text.isWeightsLabel);

export type Outcome<T> = { ok: true; value: T } | { ok: false; error: string };

function attempt<T>(name: string, call: (f: (...args: never[]) => unknown) => unknown, valid: (v: unknown) => boolean): Outcome<T> {
  const f = fn(name);
  if (!f) return { ok: false, error: "The reading isn't available yet." };
  try {
    const value = call(f);
    if (!valid(value)) return { ok: false, error: "The reading isn't available yet." };
    return { ok: true, value: value as T };
  } catch (error) {
    return { ok: false, error: error instanceof Error && error.message ? error.message : "The reading failed." };
  }
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

export function safeReadWeights(source: string): Outcome<WeightsReading> {
  return attempt<WeightsReading>(
    "readWeights",
    (f) => (f as (t: string) => unknown)(source),
    (v) => isObject(v) && isObject(v.plan) && Array.isArray(v.unread),
  );
}

/** §19.2.1: a definition with a distance in its lines is a run, logged as the phone reads it. */
export function isRunning(lines: string[]): boolean {
  const f = fn<(l: string[]) => unknown>("isRunning");
  if (f) {
    try {
      const out = f(lines);
      if (typeof out === "boolean") return out;
    } catch {
      /* fall back */
    }
  }
  return lines.some((l) => /\d\s*m\b/.test(l));
}

export function safeReadRun(lines: string[], groups: string[] = []): Outcome<RunReading> {
  return attempt<RunReading>(
    "readRun",
    (f) => (f as (l: string[], g: string[]) => unknown)(lines, groups),
    (v) => isObject(v) && Array.isArray(v.blocks) && Array.isArray(v.unread),
  );
}

export function safePublishRows(draft: PlanDraft): Outcome<PublishBody> {
  return attempt<PublishBody>(
    "publishRows",
    (f) => (f as (d: PlanDraft) => unknown)(draft),
    (v) => isObject(v) && Array.isArray(v.sessions) && Array.isArray(v.columns),
  );
}

export function safeDraftFromPublished(week: PublishedWeek, monday: string): Outcome<PlanDraft> {
  return attempt<PlanDraft>(
    "draftFromPublished",
    (f) => (f as (w: PublishedWeek, m: string) => unknown)(week, monday),
    (v) => isObject(v) && Array.isArray(v.columns) && Array.isArray(v.days) && v.days.length === 7,
  );
}

export interface DraftSession {
  date: string;
  label: string;
  sessionID: string;
  reading: WeightsReading;
  alsoToday: ReferenceSessionWire[];
}

/** Each column's week as its phones will hold it, with each session's label and unread lines. */
export function safeReadDraft(draft: PlanDraft): Outcome<{ group: string; sessions: DraftSession[] }[]> {
  return attempt<{ group: string; sessions: DraftSession[] }[]>(
    "readDraft",
    (f) => (f as (d: PlanDraft) => unknown)(draft),
    (v) => Array.isArray(v) && v.every((c) => isObject(c) && typeof c.group === "string" && Array.isArray(c.sessions)),
  );
}

/** The row group an athlete with these My columns gets (`""` for everyone's), by the package when it can. */
export function groupFor(draft: PlanDraft, groups: string[], fallback: () => string | null): string | null {
  const f = fn<(d: PlanDraft, g: string[]) => unknown>("columnsMatching");
  if (f) {
    try {
      const out = f(draft, groups);
      if (isObject(out) && typeof out.group === "string") return out.group === "" ? null : out.group;
    } catch {
      /* fall back */
    }
  }
  return fallback();
}
